import {Component, DestroyRef, ElementRef, inject, OnInit, ViewChild} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {NavigationEnd, Router, RouterLink} from '@angular/router';
import {Subscription, filter, timer} from 'rxjs';
import {ButtonModule} from 'primeng/button';
import {DialogModule} from 'primeng/dialog';
import {TranslateModule} from '@ngx-translate/core';
import {SessionService} from '@/common/auth/auth-session.service';
import {ApiResponseInterface} from '@/common/http/http.interface';
import {DateTimePipe} from '@/common/pipes/date-time/date-time.pipe';
import {SyncHealthService} from '@/pages/project/sync-health/sync-health.service';
import {SyncAlertInterface} from '@/pages/project/sync-health/sync-health.interface';
import {HeldSend, HeldSendsService} from '@/pages/project/sync-health/held-sends.service';
import {SendHeldResult, sendHeldAnswer, sendHeldRefusal, sendHeldStarted} from '@/pages/project/sync-health/send-held-result';
import {RETRY_MAX_POLLS, RETRY_POLL_INTERVAL} from '@/pages/project/sync-health/sync-polling';

/**
 * Milliseconds between two reads of the alerts: a hold shows within 15 minutes (FR-5.29, T11)
 */
export const ALERTS_POLL_INTERVAL = 5 * 60 * 1000;

/**
 * A text to translate, with its values
 */
interface Translation {
	key: string;
	params: Record<string, string>;
}

/**
 * One alert as the banner shows it
 */
interface AlertView {
	projectId: number;
	project: string;
	// "<project>: the Productive project <name> is archived", or "<project>: the link is fixed…"
	sentence: Translation;
	archivedAt: string | null;
	held: number;
	// The link is fixed and held tickets wait: the alert offers to send them (FR-5.37)
	canSend: boolean;
	// A send queued them: they stay held until their jobs run, so the alert says so instead of offering the send
	beingSent: boolean;
	// The health view's tab that the alert links to
	tab: 'held' | 'links';
}

/**
 * The result of the user's last action, in the banner's live region. It stays until the next action or page.
 */
type BannerStatus = {kind: 'sent'; project: string; result: SendHeldResult} | {kind: 'hidden'; project: string};

/**
 * A send the banner follows: the held count when it answered, and the quick reads since. Null until the read after
 * the send gives it, when no alert offered the send.
 */
interface QueuedSend {
	held: number | null;
	polls: number;
}

function alertView(alert: SyncAlertInterface, beingSent: boolean): AlertView {
	const productive = alert.productive_project_name ?? alert.productive_project_id ?? '';
	const canSend = alert.fixed && alert.held > 0;
	const state = canSend ? 'fixed' : 'archived';

	return {
		projectId: alert.project_id,
		project: alert.project_name,
		sentence: {key: `sync.alerts.${state}${productive === '' ? 'Unnamed' : ''}`, params: {project: alert.project_name, productive}},
		archivedAt: canSend ? null : alert.archived_at,
		held: alert.held,
		canSend,
		beingSent: canSend && beingSent,
		tab: alert.held > 0 ? 'held' : 'links',
	};
}

/**
 * The archived-project alerts above every page (task 4.10, FR-5.29 to FR-5.35, FR-5.37 to FR-5.39): each active
 * project the user can edit whose Productive link is archived, or that has held tickets. Each alert names the project
 * and the Productive project, gives the held count and links to the health view. After a fix, it offers to send the
 * held tickets, after a confirmation with the count (FR-3.3).
 *
 * - It reads `sync/alerts` on load and every 5 minutes, one request at a time. A failed read keeps the alerts shown.
 *   After every send answer, here or in the health view, it reads them again. While a send's tickets stay held, it
 *   reads them every few seconds until fewer are held, for a while, for each send on its own.
 * - "Hide" hides one project's alert as the user saw it, until the next sign-in (sessionStorage; sign-in and sign-out
 *   clear it). An alert that turns into a send offer shows again (the user's decision, 2026-10-30).
 * - Screen readers hear the alerts once in each sign-in, not on every page. The banner never moves focus when it
 *   appears. When an action removes the button that has focus, focus goes to the banner's heading.
 * - Without update_project, or once signed out, it reads nothing and shows nothing new.
 */
@Component({
	selector: 'app-sync-alert-banner',
	imports: [ButtonModule, DialogModule, RouterLink, TranslateModule, DateTimePipe],
	templateUrl: './sync-alert-banner.component.html',
})
export class SyncAlertBannerComponent implements OnInit {
	// The alerts shown: the ones the user hid are left out
	alerts: AlertView[] = [];
	// The alerts as they were announced, once in each sign-in
	announced: AlertView[] = [];
	status: BannerStatus | null = null;

	confirming: boolean = false;
	sending: boolean = false;
	// The alert whose send the dialog confirms, with its count as the user saw it
	confirmed: AlertView | null = null;

	@ViewChild('heading') private heading?: ElementRef<HTMLElement>;

	// The projects whose alert the user hid, and whether that alert offered the send then
	private hidden = new Map<number, boolean>();
	// The sends whose tickets stay held until their jobs run, by project: a read with fewer held ends one
	private queued = new Map<number, QueuedSend>();
	// Announced in this page: storage the browser refuses doesn't keep the flag
	private announcedHere: boolean = false;
	private request?: Subscription;
	private follow?: Subscription;
	private opener: HTMLElement | null = null;
	// The page's path: a navigation that changes only the query (the health view's tabs) keeps the action's result
	private path: string = '';
	private readonly destroyRef = inject(DestroyRef);
	private readonly host: ElementRef<HTMLElement> = inject(ElementRef);

	constructor(
		private syncHealthService: SyncHealthService,
		private sessionService: SessionService,
		private heldSends: HeldSendsService,
		private router: Router,
	) {
	}

	ngOnInit(): void {
		// FR-5.32: only whoever can edit projects sees the alerts. The API answers [] without update_project too.
		if (!this.sessionService.hasPermission('update_project')) {
			return;
		}
		this.hidden = this.readHidden();
		this.path = pathOf(this.router.url);
		timer(0, ALERTS_POLL_INTERVAL).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.read(false));
		// A send changes the held counts and can fix an alert: the banner's own send, or one in the health view
		this.heldSends.sent$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(sent => this.sendAnswered(sent));
		// An action's result belongs to the page it happened on
		this.router.events
			.pipe(filter(event => event instanceof NavigationEnd), takeUntilDestroyed(this.destroyRef))
			.subscribe(event => {
				const path = pathOf(event.urlAfterRedirects);
				if (path !== this.path) {
					this.path = path;
					this.status = null;
				}
			});
	}

	/**
	 * Hides the project's alert, as the user saw it, until the next sign-in (FR-5.39). Alerts for other projects still
	 * show.
	 */
	hide(alert: AlertView): void {
		this.hidden.set(alert.projectId, alert.canSend);
		this.writeHidden();
		this.alerts = this.alerts.filter(shown => shown.projectId !== alert.projectId);
		this.status = {kind: 'hidden', project: alert.project};
		// The button left with its alert
		this.keepFocus();
	}

	confirmSend(alert: AlertView, opener: HTMLElement): void {
		if (this.sending) {
			return;
		}
		this.confirmed = alert;
		this.opener = opener;
		this.confirming = true;
	}

	cancelSend(): void {
		this.confirming = false;
		this.dialogClosed();
	}

	/**
	 * Sends the confirmed project's held tickets. After every answer the banner, and the project's health view, read
	 * again (FR-3.8).
	 */
	send(): void {
		const alert = this.confirmed;
		this.confirming = false;
		this.dialogClosed();
		if (this.sending || alert === null) {
			return;
		}
		this.sending = true;
		this.status = null;
		this.syncHealthService.sendHeld(alert.projectId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (response: ApiResponseInterface): void => {
					this.sending = false;
					this.status = {kind: 'sent', project: alert.project, result: sendHeldAnswer(response)};
					this.heldSends.sent(alert.projectId, 'banner', sendHeldStarted(response));
				},
				error: (error: ApiResponseInterface): void => {
					this.sending = false;
					this.status = {kind: 'sent', project: alert.project, result: sendHeldRefusal(error)};
					this.heldSends.sent(alert.projectId, 'banner', false);
				},
			});
	}

	/**
	 * The dialog closed: by its buttons at once, or by its close button or Escape once it has hidden. Focus goes back
	 * to the button that opened it.
	 */
	dialogClosed(): void {
		const opener = this.opener;
		this.opener = null;
		if (opener) {
			setTimeout(() => {
				if (opener.isConnected) {
					opener.focus();

					return;
				}
				// A read removed its alert while the dialog was open: the heading, or the page when the banner went too
				const page = document.querySelector<HTMLElement>('main');
				if (!this.heading && page && !page.hasAttribute('tabindex')) {
					page.setAttribute('tabindex', '-1');
				}
				(this.heading?.nativeElement ?? page)?.focus();
			});
		}
	}

	/**
	 * A send answered, here or in the health view. When its tickets are queued, the alert says they are being sent,
	 * from the held count it has now. Then the alerts are read again.
	 */
	private sendAnswered(sent: HeldSend): void {
		const projectId = Number(sent.projectId);
		const alert = this.alerts.find(shown => shown.projectId === projectId);
		if (sent.queued) {
			const hadFocus = this.host.nativeElement.contains(document.activeElement);
			// An alert that didn't offer the send (its link read as archived, or none showed): the read below gives the count
			this.queued.set(projectId, {held: alert?.canSend ? alert.held : null, polls: 0});
			this.alerts = this.alerts.map(shown => shown.projectId === projectId ? {...shown, beingSent: true} : shown);
			// The send button gave way to "Sending…". A send in the health view keeps focus where it is.
			if (hadFocus) {
				this.keepFocus();
			}
			this.followQueued();
		}
		this.read(true);
	}

	/**
	 * Reads the alerts. One request at a time: a poll waits for the read on its way. A read after a send cancels it,
	 * because its answer can be older than the send. True when a read started.
	 */
	private read(afterSend: boolean): boolean {
		// Signed out: the layout, and this banner, go with the next navigation
		if (!this.sessionService.isAuthenticated.value) {
			return false;
		}
		if (this.request && !this.request.closed) {
			if (!afterSend) {
				return false;
			}
			this.request.unsubscribe();
		}
		this.request = this.syncHealthService.alerts()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (response: ApiResponseInterface): void => {
					// A 403 and a 422 arrive as answers with success false: like an error, they keep the alerts shown
					if (response.success && Array.isArray(response.data)) {
						this.show(response.data as SyncAlertInterface[]);
					}
				},
				error: (): void => undefined,
			});

		return true;
	}

	private show(alerts: SyncAlertInterface[]): void {
		// An answer after the sign-out: the sign-out removed the flags this would write
		if (!this.sessionService.isAuthenticated.value) {
			return;
		}
		const hadFocus = this.host.nativeElement.contains(document.activeElement);
		// A send is done with once its alert went, isn't fixed any more, or counts fewer held tickets
		for (const [projectId, send] of this.queued) {
			const alert = alerts.find(shown => shown.project_id === projectId);
			send.held ??= alert?.held ?? null;
			if (!alert || !alert.fixed || alert.held < (send.held ?? alert.held)) {
				this.queued.delete(projectId);
			}
		}
		this.alerts = alerts
			.map(alert => alertView(alert, this.queued.has(alert.project_id)))
			.filter(alert => this.shows(alert));
		// Once in each sign-in, with the first alerts there are (FR-5.48)
		if (this.alerts.length > 0 && !this.wasAnnounced()) {
			this.announced = this.alerts;
			this.announcedHere = true;
			this.store(this.sessionService.SYNC_ALERTS_ANNOUNCED_KEY, '1');
		}
		if (hadFocus) {
			this.keepFocus();
		}
	}

	/**
	 * A hidden alert stays hidden, unless it offers the send now and didn't when the user hid it: it shows again, once
	 */
	private shows(alert: AlertView): boolean {
		const hiddenCanSend = this.hidden.get(alert.projectId);
		if (hiddenCanSend === undefined) {
			return true;
		}
		if (alert.canSend && !hiddenCanSend) {
			this.hidden.delete(alert.projectId);
			this.writeHidden();

			return true;
		}

		return false;
	}

	/**
	 * While a send's tickets stay held, the alerts are read every few seconds. Each send has its own limit: after its
	 * last read, its alert offers the send again.
	 */
	private followQueued(): void {
		if (this.follow && !this.follow.closed) {
			return;
		}
		this.follow = timer(RETRY_POLL_INTERVAL, RETRY_POLL_INTERVAL)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				if (this.queued.size === 0) {
					this.follow?.unsubscribe();

					return;
				}
				// A read still on its way: this tick doesn't count
				if (!this.read(false)) {
					return;
				}
				for (const [projectId, send] of this.queued) {
					send.polls++;
					if (send.polls >= RETRY_MAX_POLLS) {
						this.queued.delete(projectId);
						this.alerts = this.alerts.map(alert => alert.projectId === projectId ? {...alert, beingSent: false} : alert);
					}
				}
			});
	}

	/**
	 * Once the page shows the change: when the element that had focus left the banner (its alert went), focus goes to
	 * the banner's heading. The dialog renders inside the banner (PrimeNG's overlayAppendTo is 'self'), so focus in it
	 * stays there.
	 */
	private keepFocus(): void {
		setTimeout(() => {
			if (!this.host.nativeElement.contains(document.activeElement)) {
				this.heading?.nativeElement.focus();
			}
		});
	}

	/**
	 * The hidden alerts, as `[{id, canSend}]`. A value that isn't one, or storage the browser refuses, hides nothing.
	 */
	private readHidden(): Map<number, boolean> {
		try {
			const entries: unknown = JSON.parse(sessionStorage.getItem(this.sessionService.SYNC_ALERTS_HIDDEN_KEY) ?? '[]');

			return new Map((Array.isArray(entries) ? entries : [])
				.filter((entry): entry is {id: number; canSend: unknown} => typeof entry?.id === 'number')
				.map(entry => [entry.id, entry.canSend === true]));
		} catch {
			return new Map();
		}
	}

	private writeHidden(): void {
		this.store(this.sessionService.SYNC_ALERTS_HIDDEN_KEY, JSON.stringify([...this.hidden].map(([id, canSend]) => ({id, canSend}))));
	}

	/**
	 * Also in this page when the browser refuses storage: that loses the flag across pages only
	 */
	private wasAnnounced(): boolean {
		if (this.announcedHere) {
			return true;
		}
		try {
			return sessionStorage.getItem(this.sessionService.SYNC_ALERTS_ANNOUNCED_KEY) !== null;
		} catch {
			return false;
		}
	}

	/**
	 * Storage the browser refuses (blocked, full) keeps the state for this page only
	 */
	private store(key: string, value: string): void {
		try {
			sessionStorage.setItem(key, value);
		} catch {
			// Nothing to do: the banner works without it
		}
	}
}

/**
 * A URL without its query and fragment
 */
function pathOf(url: string): string {
	return url.split(/[?#]/)[0];
}
